"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.round2 = exports.round = void 0;
/**
 * CALL-783: money/number rounding for the budget + scenario calcs. Lives in @callmart/types because
 * BOTH the backend (which persists the figures) and the frontend preview grids round with it — the
 * same reason `budgetExtrapolation` and `scenarioTraining` live here. It used to be a backend module
 * plus a hand-copied one-liner in `frontend/src/components/Budget/budgetCalculation.ts`, which meant
 * a fix on one side silently desynced the client preview from the persisted server result.
 *
 * Rounding mode: HALF AWAY FROM ZERO, i.e. Excel's ROUND — the board's figures come from Excel, so
 * -1.005 must book as -1.01, not -1.00. Plain `Math.round` breaks ties toward +infinity, which is
 * correct for positives and wrong for negatives (it rounds their magnitude DOWN), and the epsilon
 * nudge below makes negatives worse still because it moves them TOWARD zero. So the correction is
 * applied to the MAGNITUDE and the sign is restored afterwards. Negative inputs are reachable on
 * every scenario surface: contact deltas, minute-price cuts, budget reductions and driver deltas may
 * all be negative (e.g. driverDelta -1000 x wl 1.5 x cpc 8.25 = exactly -12.375 -> -12.38).
 *
 * `+ Number.EPSILON` before scaling nudges values whose float representation sits just below the
 * .xx5 boundary (0.145 -> 14.499999999999998) up to it, so they round half-up like Excel does
 * instead of down. NOTE its limitation: Number.EPSILON is ~2.2e-16 in ABSOLUTE terms (it is the ulp
 * at 1.0), so it stops nudging anything once the value's magnitude grows past ~1 — at magnitude 12
 * the ulp is already ~1.8e-15 and `value + Number.EPSILON === value`. The epsilon therefore only
 * rescues small values; larger ones are decided by the float representation alone. That is
 * pre-existing and orthogonal to the sign fix (a real fix would need decimal arithmetic), so it is
 * deliberately left as is — do not assume the epsilon is doing more than this.
 */
const round = (value, decimals) => {
    const factor = 10 ** decimals;
    // Round the magnitude, then restore the sign, so ties always go away from zero.
    const rounded = Math.round((Math.abs(value) + Number.EPSILON) * factor) / factor;
    // Normalise -0 (and any negative value that rounds to zero) to 0 — a "-0,00 EUR" cell or a -0 in
    // an Excel export is its own small embarrassment. NaN/+-Infinity fall through unchanged.
    if (rounded === 0)
        return 0;
    return value < 0 ? -rounded : rounded;
};
exports.round = round;
/** Euro/percent rounding — the default across the budget and scenario calcs. */
const round2 = (value) => (0, exports.round)(value, 2);
exports.round2 = round2;
